import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ClaimPolicyCode,
  ClaimPolicyVisibility,
} from '../../../../models/api/reports/claim-policy-filter';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { ReportsService } from '../../../../services/reports/reports.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { InputComponent } from '../../../core/controls/input.component';
import { PickListComponent } from '../../../core/controls/pick-list.component';
import { FieldContainerComponent } from '../../../core/fields/field-container.component';
import { FilterSection, ICriteria } from '../edit-filter.component';

@Component({
  selector: 'app-claim-policy-criteria',
  imports: [
    CommonModule,
    PickListComponent,
    InputComponent,
    ButtonComponent,
    FieldContainerComponent,
  ],
  template: `
    <div class="tw-pt-2">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        @if (visibility?.showPolicy) {
          <div>
            <app-field-container
              label="Search"
              [control]="fg.controls.policySearch"
            >
              <ng-template #value>
                <div
                  class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 tw-w-full tw-min-w-0"
                >
                  <div class="tw-flex-1 tw-min-w-0">
                    <app-input
                      [control]="fg.controls.policySearch"
                      (keypress)="searchKeyPress($event, 'POLICY')"
                    />
                  </div>
                  <div class="tw-pt-1 tw-shrink-0">
                    <app-button
                      label="Search"
                      (click)="search('POLICY')"
                      [disabled]="
                        !fg.controls.policySearch.value ||
                        fg.controls.policySearch.value.length < 3
                      "
                      [loading]="searchingPolicy"
                    />
                  </div>
                </div>
              </ng-template>
            </app-field-container>

            <ng-container
              *ngTemplateOutlet="
                labeledPickList;
                context: {
                  label: 'Policy source',
                  sourceControl: fg.controls.availablePolicySources,
                  targetControl: fg.controls.selectedPolicySources,
                }
              "
            ></ng-container>
          </div>
        }

        @if (visibility?.showExt) {
          <div>
            <app-field-container
              label="Search"
              [control]="fg.controls.extSearch"
            >
              <ng-template #value>
                <div
                  class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 tw-w-full tw-min-w-0"
                >
                  <div class="tw-flex-1 tw-min-w-0">
                    <app-input
                      [control]="fg.controls.extSearch"
                      (keypress)="searchKeyPress($event, 'EXTSOURCE')"
                    />
                  </div>
                  <div class="tw-pt-1 tw-shrink-0">
                    <app-button
                      label="Search"
                      (click)="search('EXTSOURCE')"
                      [disabled]="
                        !fg.controls.extSearch.value ||
                        fg.controls.extSearch.value.length < 3
                      "
                      [loading]="searchingExt"
                    />
                  </div>
                </div>
              </ng-template>
            </app-field-container>

            <ng-container
              *ngTemplateOutlet="
                labeledPickList;
                context: {
                  label: 'Ext-SourceSystemRecord',
                  sourceControl: fg.controls.availableExtSources,
                  targetControl: fg.controls.selectedExtSources,
                }
              "
            ></ng-container>
          </div>
        }

        @if (visibility?.showLegacy) {
          <div>
            <app-field-container
              label="Search"
              [control]="fg.controls.legacySearch"
            >
              <ng-template #value>
                <div
                  class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 tw-w-full tw-min-w-0"
                >
                  <div class="tw-flex-1 tw-min-w-0">
                    <app-input
                      [control]="fg.controls.legacySearch"
                      (keypress)="searchKeyPress($event, 'LEGACY')"
                    />
                  </div>
                  <div class="tw-pt-1 tw-shrink-0">
                    <app-button
                      label="Search"
                      (click)="search('LEGACY')"
                      [disabled]="
                        !fg.controls.legacySearch.value ||
                        fg.controls.legacySearch.value.length < 3
                      "
                      [loading]="searchingLegacy"
                    />
                  </div>
                </div>
              </ng-template>
            </app-field-container>

            <ng-container
              *ngTemplateOutlet="
                labeledPickList;
                context: {
                  label: 'Legacy producer code',
                  sourceControl: fg.controls.availableLegacyCodes,
                  targetControl: fg.controls.selectedLegacyCodes,
                }
              "
            ></ng-container>
          </div>
        }
      </div>

      <ng-template
        #labeledPickList
        let-label="label"
        let-sourceControl="sourceControl"
        let-targetControl="targetControl"
      >
        <div class="tw-flex tw-flex-col">
          <div class="tw-font-bold tw-pr-1">{{ label }}</div>
          <div class="tw-flex tw-justify-center tw-pt-4">
            <app-pick-list
              [sourceControl]="sourceControl"
              [targetControl]="targetControl"
              labelName="displayName"
              keyName="id"
              [showMove]="false"
              [preserveOrder]="false"
              [showAddRemoveAll]="true"
              (targetChanged)="emitCount()"
              listClasses="tw-w-[320px]"
            />
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styles: [
    `
      :host ::ng-deep app-field-container > div > div:first-child {
        flex-basis: 100% !important;
      }
      :host ::ng-deep app-field-container > div > div:nth-child(2) {
        flex-basis: 100% !important;
      }

      @media (min-width: 768px) {
        :host ::ng-deep app-field-container > div > div:first-child {
          flex-basis: 130px !important;
          flex-shrink: 0 !important;
          flex-grow: 0 !important;
        }
        :host ::ng-deep app-field-container > div > div:nth-child(2) {
          flex-basis: 0 !important;
          flex-grow: 1 !important;
          min-width: 0 !important;
        }
        :host ::ng-deep app-input {
          display: block;
          min-width: 0;
        }
      }
    `,
  ],
})
export class ClaimPolicyCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  private reports = inject(ReportsService);

  @Input({ required: true })
  section: FilterSection;

  @Input()
  visibility: ClaimPolicyVisibility;

  contractNumber: string;

  searchingPolicy = false;
  searchingExt = false;
  searchingLegacy = false;

  fg = new FormGroup({
    // Policy Source
    policySearch: new FormControl<string>(null, [Validators.minLength(3)]),
    availablePolicySources: new FormControl<PolicyItem[]>([]),
    selectedPolicySources: new FormControl<PolicyItem[]>([]),

    // Ext_SourceSystemRecord
    extSearch: new FormControl<string>(null, [Validators.minLength(3)]),
    availableExtSources: new FormControl<PolicyItem[]>([]),
    selectedExtSources: new FormControl<PolicyItem[]>([]),

    // Legacy Producer Code
    legacySearch: new FormControl<string>(null, [Validators.minLength(3)]),
    availableLegacyCodes: new FormControl<PolicyItem[]>([]),
    selectedLegacyCodes: new FormControl<PolicyItem[]>([]),
  });

  constructor() {
    super();
  }

  async setClaimPolicyFromCriteria(criteria: FilterCriteria[]) {
    const policyCriteria = criteria.filter(
      (c) => c.tableAlias === 'claim_policy' && c.column === 'POLICY_SOURCE',
    );
    if (policyCriteria.length) {
      const items: PolicyItem[] = policyCriteria.flatMap((c) =>
        c.values.map((v) => ({
          id: v,
          displayName: v,
          tableName: 'claim_policy',
        })),
      );
      this.fg.controls.selectedPolicySources.setValue(items);
    }

    const legacyCriteria = criteria
      .filter((c) => c.tableAlias === 'LEGACY' && c.column === 'ATTRIB_VALUE')
      .flatMap((c) => c.values);

    const extCriteria = criteria
      .filter((c) => c.tableAlias === 'EXT' && c.column === 'ATTRIB_VALUE')
      .flatMap((c) => c.values);

    if (extCriteria.length) {
      const results = await this.api.filtersCriteria.getClaimPolicyDescriptions(
        this.contractNumber,
        extCriteria,
        'EXT',
      );
      this.fg.controls.selectedExtSources.setValue(
        this.mapResultsToItems(results, 'EXTSOURCE'),
      );
    }

    if (legacyCriteria.length) {
      const results = await this.api.filtersCriteria.getClaimPolicyDescriptions(
        this.contractNumber,
        legacyCriteria,
        'LEGACY',
      );
      this.fg.controls.selectedLegacyCodes.setValue(
        this.mapResultsToItems(results, 'LEGACY'),
      );
    }

    this.emitCount();
  }

  async ngOnInit() {
    this.section.criteria = this;
    this.setupValueChangeSubscriptions();

    const client = this.reports.selectedClient();
    if (client) {
      this.contractNumber = client;
    }

    if (this.section.values?.length) {
      await this.setClaimPolicyFromCriteria(this.section.values);
    }
  }

  private setupValueChangeSubscriptions() {
    this.dispose.subs =
      this.fg.controls.selectedPolicySources.valueChanges.subscribe(() =>
        this.emitCount(),
      );
    this.dispose.subs =
      this.fg.controls.selectedExtSources.valueChanges.subscribe(() =>
        this.emitCount(),
      );
    this.dispose.subs =
      this.fg.controls.selectedLegacyCodes.valueChanges.subscribe(() =>
        this.emitCount(),
      );
  }

  emitCount() {
    const totalCount =
      (this.fg.controls.selectedPolicySources.value?.length || 0) +
      (this.fg.controls.selectedExtSources.value?.length || 0) +
      (this.fg.controls.selectedLegacyCodes.value?.length || 0);
    this.section.count.set(totalCount);
  }

  valid(): boolean {
    return true;
  }

  searchKeyPress(event: KeyboardEvent, codeType: CodeType) {
    if (event.key === 'Enter') {
      this.search(codeType);
    }
  }

  async search(codeType: CodeType) {
    const searchControl = this.getSearchControl(codeType);
    const searchText = searchControl.value;

    if (!searchText || searchText.length < 3) {
      return;
    }

    this.setSearching(codeType, true);

    const results = await this.api.filtersCriteria.searchClaimPolicyCodes(
      this.contractNumber,
      searchText,
      codeType,
    );

    const items = this.mapResultsToItems(results, codeType);
    this.getAvailableControl(codeType).setValue(items);
    this.setSearching(codeType, false);
  }

  private getSearchControl(codeType: CodeType): FormControl<string> {
    switch (codeType) {
      case 'POLICY':
        return this.fg.controls.policySearch;
      case 'EXTSOURCE':
        return this.fg.controls.extSearch;
      case 'LEGACY':
        return this.fg.controls.legacySearch;
    }
  }

  private getAvailableControl(codeType: CodeType): FormControl<PolicyItem[]> {
    switch (codeType) {
      case 'POLICY':
        return this.fg.controls.availablePolicySources;
      case 'EXTSOURCE':
        return this.fg.controls.availableExtSources;
      case 'LEGACY':
        return this.fg.controls.availableLegacyCodes;
    }
  }

  private setSearching(codeType: CodeType, value: boolean) {
    switch (codeType) {
      case 'POLICY':
        this.searchingPolicy = value;
        break;
      case 'EXTSOURCE':
        this.searchingExt = value;
        break;
      case 'LEGACY':
        this.searchingLegacy = value;
        break;
    }
  }

  private mapResultsToItems(
    results: ClaimPolicyCode[],
    codeType: CodeType,
  ): PolicyItem[] {
    const showDescription = codeType !== 'POLICY';

    return results.map((r) => ({
      id: r.code,
      displayName:
        showDescription && r.description
          ? `${r.code} - ${r.description}`
          : r.code,
      tableName: r.tableName,
    }));
  }

  getCriteria(): FilterCriteria[] {
    return [
      ...this.getPolicySourceCriteria(),
      ...this.getGroupedCriteria(
        this.fg.controls.selectedExtSources.value || [],
        'EXT',
      ),
      ...this.getGroupedCriteria(
        this.fg.controls.selectedLegacyCodes.value || [],
        'LEGACY',
      ),
    ];
  }

  private getPolicySourceCriteria(): FilterCriteria[] {
    const items = this.fg.controls.selectedPolicySources.value || [];
    if (items.length === 0) {
      return [];
    }

    return [
      ({
        tableAlias: 'claim_policy',
        column: 'POLICY_SOURCE',
        displayName: '',
        operator: 'LIKE',
        values: items.map((item) => item.id),
        lineOfBusiness: '',
        dataType: 'VARCHAR2',
        isClaimLab: false,
        formattedOperator: '',
        formattedValue: '',
        filterSection: 'Claim Policy',
        filterSubsection: 'Policy Source',
      } as FilterCriteria),
    ];
  }

  private getGroupedCriteria(
    items: PolicyItem[],
    suffix: 'EXT' | 'LEGACY',
  ): FilterCriteria[] {
    if (items.length === 0) {
      return [];
    }

    return [
      ({
        tableAlias: items[0].tableName,
        column: 'ATTRIB_VALUE',
        displayName: '',
        operator: 'LIKE',
        values: items.map((item) => `${item.id}|${suffix}`),
        lineOfBusiness: '',
        dataType: 'VARCHAR2',
        isClaimLab: false,
        formattedOperator: '',
        formattedValue: '',
        filterSection: 'Claim Policy',
        filterSubsection: '',
      } as FilterCriteria),
    ];
  }

  clearCriteria(): void {
    this.fg.controls.policySearch.setValue(null, { emitEvent: false });
    this.fg.controls.availablePolicySources.setValue([]);
    this.fg.controls.selectedPolicySources.setValue([]);

    this.fg.controls.extSearch.setValue(null, { emitEvent: false });
    this.fg.controls.availableExtSources.setValue([]);
    this.fg.controls.selectedExtSources.setValue([]);

    this.fg.controls.legacySearch.setValue(null, { emitEvent: false });
    this.fg.controls.availableLegacyCodes.setValue([]);
    this.fg.controls.selectedLegacyCodes.setValue([]);
  }
}

interface PolicyItem {
  id: string;
  displayName: string;
  tableName: string;
}

type CodeType = 'POLICY' | 'EXTSOURCE' | 'LEGACY';
